import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { ActionLink } from '@atlassian/jira-admin-pages-common/src/ui/actions-cell/types';
import type { IssueTypeScreenScheme } from '@atlassian/jira-admin-screens-services/src/common/types.tsx';
import {
	buildConfigureIssueTypeScreenSchemeJSPUrl,
	buildEditIssueTypeScreenSchemeJSPUrl,
	buildCopyIssueTypeScreenSchemeJSPUrl,
	buildDeleteIssueTypeScreenSchemeJSPUrl,
} from '@atlassian/jira-admin-screens-services/src/services/issue-type-screen-schemes-service/actions/utils.tsx';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import messages from './messages';

export const createHead = (formatMessage: (arg1: MessageDescriptor) => string) => ({
	cells: [
		{
			key: 'name',
			content: formatMessage(messages.name),
			isSortable: true,
			width: 50,
		},
		{
			key: 'projects',
			content: formatMessage(messages.projects),
			width: 40,
		},
		{
			key: 'actions',
			content: formatMessage(messages.actions),
			width: 10,
		},
	],
});

export const buildActionLinks = (
	issueTypeScreenScheme: IssueTypeScreenScheme,
	formatMessage: FormatMessage,
): ActionLink[] => {
	const canDelete =
		issueTypeScreenScheme.projects?.total != null && issueTypeScreenScheme.projects.total === 0;
	return [
		{
			displayMessage: formatMessage(messages.configure),
			href: buildConfigureIssueTypeScreenSchemeJSPUrl(issueTypeScreenScheme.id),
			onItemClickHandleAnalytics: (analyticsEvent: UIAnalyticsEvent) =>
				fireUIAnalytics(analyticsEvent, 'configure'),
		},
		{
			displayMessage: formatMessage(messages.edit),
			href: buildEditIssueTypeScreenSchemeJSPUrl(issueTypeScreenScheme.id),
			onItemClickHandleAnalytics: (analyticsEvent: UIAnalyticsEvent) =>
				fireUIAnalytics(analyticsEvent, 'edit'),
		},
		{
			displayMessage: formatMessage(messages.copy),
			href: buildCopyIssueTypeScreenSchemeJSPUrl(issueTypeScreenScheme.id),
			onItemClickHandleAnalytics: (analyticsEvent: UIAnalyticsEvent) =>
				fireUIAnalytics(analyticsEvent, 'copy'),
		},
		...(canDelete
			? [
					{
						displayMessage: formatMessage(messages.delete),
						href: buildDeleteIssueTypeScreenSchemeJSPUrl(issueTypeScreenScheme.id),
						onItemClickHandleAnalytics: (analyticsEvent: UIAnalyticsEvent) =>
							fireUIAnalytics(analyticsEvent, 'delete'),
					},
				]
			: []),
	];
};

export const getAnalyticsAttributes = (
	issueTypeScreenScheme: IssueTypeScreenScheme,
): {
	[key: string]: string | number | boolean | undefined;
} => ({
	issueTypeScreenSchemeId: `${issueTypeScreenScheme.id}`,
	issueTypeScreenSchemeHasDescription: !!issueTypeScreenScheme.description,
	projectsCount: issueTypeScreenScheme.projects?.total,
});
